import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, AlertCircle } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductReviews = ({ productId }) => {
  const { language, isAuthenticated, token } = useStore();
  const t = translations[language];
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average_rating: 0, rating_distribution: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: '',
  });

  useEffect(() => {
    loadReviews();
  }, [productId]);

  const loadReviews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}/reviews`);
      const data = await response.json();
      setReviews(data.reviews || []);
      setStats({
        count: data.count || 0,
        average_rating: data.average_rating || 0,
        rating_distribution: data.rating_distribution || {},
      });
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          ...formData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error submitting review');
      }

      setSuccess(true);
      setShowForm(false);
      setFormData({ rating: 5, title: '', comment: '' });
      loadReviews();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    try {
      await fetch(`${BACKEND_URL}/api/reviews/${reviewId}/helpful`, {
        method: 'POST',
      });
      loadReviews();
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const StarRating = ({ rating, interactive = false, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onChange(star) : undefined}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-20 bg-gray-200 rounded mb-4"></div>
      </div>
    );
  }

  return (
    <div className="mt-12 border-t pt-8" data-testid="product-reviews">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        {language === 'fr' ? 'Avis Clients' : 'Klantbeoordelingen'}
      </h2>

      {/* Stats Summary */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Average Rating */}
          <div className="text-center">
            <div className="text-5xl font-bold text-amber-700">
              {stats.average_rating.toFixed(1)}
            </div>
            <StarRating rating={Math.round(stats.average_rating)} />
            <p className="text-sm text-gray-500 mt-2">
              {stats.count} {language === 'fr' ? 'avis' : 'beoordelingen'}
            </p>
          </div>

          {/* Rating Distribution */}
          <div className="flex-1">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.rating_distribution[star] || 0;
              const percentage = stats.count > 0 ? (count / stats.count) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2 mb-1">
                  <span className="text-sm w-8">{star} ★</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-8">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Write Review Button */}
        {isAuthenticated && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="mt-6 bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {language === 'fr' ? '\u00c9crire un avis' : 'Schrijf een beoordeling'}
          </button>
        )}

        {!isAuthenticated && (
          <p className="mt-6 text-sm text-gray-500">
            {language === 'fr'
              ? 'Connectez-vous pour laisser un avis (achat v\u00e9rifi\u00e9 requis)'
              : 'Log in om een beoordeling te schrijven (geverifieerde aankoop vereist)'}
          </p>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {language === 'fr' ? 'Merci pour votre avis !' : 'Bedankt voor uw beoordeling!'}
        </div>
      )}

      {/* Review Form */}
      {showForm && (
        <form onSubmit={handleSubmitReview} className="mb-8 bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {language === 'fr' ? 'Votre avis' : 'Uw beoordeling'}
          </h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'fr' ? 'Note' : 'Score'}
            </label>
            <StarRating
              rating={formData.rating}
              interactive={true}
              onChange={(rating) => setFormData({ ...formData, rating })}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'fr' ? 'Titre' : 'Titel'}
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder={language === 'fr' ? 'R\u00e9sumez votre avis' : 'Vat uw mening samen'}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'fr' ? 'Commentaire' : 'Opmerking'}
            </label>
            <textarea
              required
              rows={4}
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none"
              placeholder={
                language === 'fr'
                  ? 'Partagez votre exp\u00e9rience avec ce produit...'
                  : 'Deel uw ervaring met dit product...'
              }
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {submitting
                ? t.common.loading
                : language === 'fr'
                ? 'Publier'
                : 'Publiceren'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.admin.cancel}
            </button>
          </div>
        </form>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {language === 'fr'
            ? 'Aucun avis pour le moment. Soyez le premier !'
            : 'Nog geen beoordelingen. Wees de eerste!'}
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border-b pb-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StarRating rating={review.rating} />
                    <span className="font-semibold text-gray-900">{review.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{review.user_name}</span>
                    <span>\u2022</span>
                    <span>{new Date(review.created_at).toLocaleDateString()}</span>
                    {review.verified_purchase && (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                        \u2713 {language === 'fr' ? 'Achat v\u00e9rifi\u00e9' : 'Geverifieerde aankoop'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mt-2">{review.comment}</p>
              <button
                onClick={() => handleMarkHelpful(review.id)}
                className="mt-3 flex items-center gap-1 text-sm text-gray-500 hover:text-amber-700 transition-colors"
              >
                <ThumbsUp className="w-4 h-4" />
                {language === 'fr' ? 'Utile' : 'Nuttig'} ({review.helpful_count || 0})
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
